/**
 * Plugin Provider
 *
 * Central plugin management system that handles:
 * - Lazy loading of plugins
 * - Plugin lifecycle (init/destroy)
 * - Conditional rendering based on enabled state
 */

import type { FC } from "react";

import { createContext, useEffect, useState, Suspense, ReactNode } from "react";
import { PluginConfig, Plugin, PluginContextValue } from "./types";

export const PluginContext = createContext<PluginContextValue | null>(null);

interface PluginProviderProps {
    plugins: PluginConfig[];
    children: ReactNode;
}

export const PluginProvider: FC<PluginProviderProps> = ({ plugins, children }) => {
    const [loadedPlugins, setLoadedPlugins] = useState<Map<string, Plugin>>(new Map());
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const loadPlugins = async () => {
            const loaded = new Map<string, Plugin>();

            for (const pluginConfig of plugins) {
                // Skip disabled plugins
                if (!pluginConfig.enabled) {
                    console.info(`🔌 Plugin "${pluginConfig.name}" is disabled, skipping`);
                    continue;
                }

                try {
                    console.info(`🔌 Loading plugin: ${pluginConfig.name}`);

                    // Lazy load the plugin module
                    const module = await pluginConfig.loader();

                    if (!isMounted) return;

                    const PluginComponent = module.default;

                    const plugin: Plugin = {
                        name: pluginConfig.name,
                        component: PluginComponent,
                        init: module.init,
                        destroy: module.destroy,
                        update: module.update,
                    };

                    // Call init lifecycle hook if available
                    if (plugin.init) {
                        await plugin.init();
                    }

                    loaded.set(pluginConfig.name, plugin);
                    console.info(`✅ Plugin "${pluginConfig.name}" loaded successfully`);
                } catch (error) {
                    console.error(`❌ Failed to load plugin: ${pluginConfig.name}`, error);
                }
            }

            if (isMounted) {
                setLoadedPlugins(loaded);
                setIsLoading(false);
            }
        };

        loadPlugins();

        // Cleanup: destroy plugins on unmount
        return () => {
            isMounted = false;
            loadedPlugins.forEach((plugin) => {
                if (plugin.destroy) {
                    console.info(`🧹 Destroying plugin: ${plugin.name}`);
                    plugin.destroy();
                }
            });
        };
    }, [plugins]);

    const contextValue: PluginContextValue = {
        plugins: loadedPlugins,
        isPluginLoaded: (name) => loadedPlugins.has(name),
        getPlugin: (name) => loadedPlugins.get(name),
    };

    return (
        <PluginContext.Provider value={contextValue}>
            {children}
            {/* Render loaded plugins */}
            {!isLoading &&
                Array.from(loadedPlugins.values()).map((plugin) => {
                    const Component = plugin.component;
                    return (
                        <Suspense key={plugin.name} fallback={null}>
                            <Component />
                        </Suspense>
                    );
                })}
        </PluginContext.Provider>
    );
};
